import { Injectable } from '@angular/core';

import { AvTelemetryEnvelope } from '../models/av-telemetry.model';
import { AvTelemetryLogService } from './av-telemetry-log.service';
import { AvTelemetrySamplerService } from './av-telemetry-sampler.service';
import { summariseTicks } from './av-telemetry-summariser';

/**
 * Drains the sampler on a fixed interval, reports an aggregate, and releases the records.
 *
 * This is the SINGLE DRAIN OWNER. `AvTelemetrySamplerService.drain()` empties the buffer, so
 * two independent callers would race and each would see only part of the session. When the
 * uploader lands (294679) it must receive records from here rather than draining itself.
 *
 * Draining is what bounds memory. The sampler's own cap (5,000 records) is a backstop for a
 * stalled reporter, not the normal ceiling: in steady state the buffer holds one interval's
 * worth — ~60 records at Chime's 1 Hz — and is then freed.
 *
 * Ticks are aggregated to min/max because retaining every payload is what made the previous
 * debug hook grow without bound. Events are NOT aggregated: they are rare and carry the
 * disruption detail an investigation actually reads.
 *
 * Reports go to the AV client-log endpoint — see AvTelemetryLogService — not Application
 * Insights. Metrics ride as a JSON payload at the END of the log message rather than as
 * structured properties: per-metric names are vendor-defined and would otherwise create an
 * unbounded set of log fields, the same reason event attributes are not spread into
 * dimensions. A fixed prefix plus trailing JSON is what lets Kusto pull them back out with
 * `parse_json`, so the payload must stay last with nothing appended after it.
 */
@Injectable({ providedIn: 'root' })
export class AvTelemetryReporterService {
  static readonly REPORT_INTERVAL_MS = 60_000;

  static readonly SUMMARY_MESSAGE_PREFIX = 'AvTelemetrySummary';
  static readonly DISRUPTION_MESSAGE_PREFIX = 'AvTelemetryEvent';

  /** Divides the human-readable headline from the JSON payload, which is always last. */
  static readonly PAYLOAD_SEPARATOR = ' | ';

  /** Caps the group-key list so a large session cannot bloat the message. */
  static readonly MAX_REPORTED_GROUP_KEYS = 8;

  private sampler: AvTelemetrySamplerService | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly avTelemetryLogService: AvTelemetryLogService) {}

  /** e.g. "1min" — reflects the configured cadence, not the length of any one batch. */
  private get intervalLabel(): string {
    return `${AvTelemetryReporterService.REPORT_INTERVAL_MS / 60_000}min`;
  }

  start(sampler: AvTelemetrySamplerService): void {
    if (this.intervalId !== null) {
      return;
    }

    this.sampler = sampler;

    this.intervalId = setInterval(
      () => this.report(),
      AvTelemetryReporterService.REPORT_INTERVAL_MS
    );
  }

  /** Final report before teardown, so the session's tail is not lost. */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.sampler) {
      this.report();
      this.sampler = null;
    }
  }

  /** Exposed for the interval and for tests; safe to call when nothing is buffered. */
  report(): void {
    const records = this.sampler?.drain() ?? [];
    if (!records.length) {
      return;
    }

    const ticks = records.filter((record) => record.type === 'tick');
    const events = records.filter((record) => record.type === 'event');

    this.reportSummary(records, ticks, events.length);
    events.forEach((event) => this.reportDisruption(event));
  }

  private reportSummary(
    records: readonly AvTelemetryEnvelope[],
    ticks: readonly AvTelemetryEnvelope[],
    eventCount: number
  ): void {
    const summary = summariseTicks(ticks);
    const spine = records[0];

    this.emit(
      AvTelemetryReporterService.SUMMARY_MESSAGE_PREFIX,
      // Derived, not literal, so a new vendor or a changed cadence stays honest. `tickCount`
      // remains the truth for the tail batch, which is shorter than the cadence.
      `${spine.vendor} ${this.intervalLabel} aggregate, ${summary.tickCount} ticks, ${eventCount} events`,
      {
        schemaVersion: spine.schemaVersion,
        vendor: spine.vendor,
        vendorVersion: spine.vendorVersion,
        interval: this.intervalLabel,
        tickCount: summary.tickCount,
        eventCount: eventCount,
        // Session-cumulative, NOT per-interval — read straight off the sampler, which only
        // zeroes it on start(). Aggregate with max(), never sum(): summing a monotonic
        // counter across intervals multiplies it. Named "total" so the query is obvious.
        droppedTotal: this.sampler?.droppedRecordCount ?? 0,
        maxGroupFanOut: summary.maxGroupFanOut,
        groupKeyCount: summary.groupKeys.length,
        groupKeys: summary.groupKeys.slice(
          0,
          AvTelemetryReporterService.MAX_REPORTED_GROUP_KEYS
        ),
        // Nested per metric rather than flattened to `name.min` keys: dotted property names
        // are awkward to address in KQL, and the nested shape is what parse_json returns.
        metrics: summary.metrics,
      }
    );
  }

  /**
   * Events ship whole and one per message. `attributes` nests inside the JSON payload rather
   * than being promoted to its own fields, because its keys are vendor-defined and vary per
   * event name.
   */
  private reportDisruption(event: AvTelemetryEnvelope): void {
    const payload = event.payload as {
      name?: string;
      attributes?: Record<string, unknown>;
    };
    const eventName = payload?.name ?? 'unknown';

    this.emit(
      AvTelemetryReporterService.DISRUPTION_MESSAGE_PREFIX,
      `${event.vendor} ${eventName}`,
      {
        schemaVersion: event.schemaVersion,
        vendor: event.vendor,
        vendorVersion: event.vendorVersion,
        eventName: eventName,
        seq: event.seq,
        occurredAt: event.occurredAt,
        attributes: payload?.attributes ?? {},
      }
    );
  }

  /**
   * `<prefix>: <headline> | <json>` — the payload is last so a Kusto `extract` can anchor on
   * the trailing brace.
   *
   * A failed post is dropped, not retried and not surfaced: a telemetry sink must never
   * disrupt the session it observes, and this sink is interim.
   */
  private emit(prefix: string, headline: string, payload: unknown): void {
    const message = [
      `${prefix}: ${headline}`,
      JSON.stringify(payload),
    ].join(AvTelemetryReporterService.PAYLOAD_SEPARATOR);

    this.avTelemetryLogService.send(message).subscribe({ error: () => undefined });
  }
}
